import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import PitchAngleVisualization from "@/components/PitchAngleVisualization";
import { ImageMeasurementGradingSection } from "@/components/ImageMeasurementGradingSection";
import { getTraitColor, getTraitIcon } from "@/lib/traitIcons";
import { clampValue, cn, isDuplicateName } from "@/lib/utils";
import { isEntryTypeExcluded } from "@/constants/entryConfigVisibility";
import {
  IMAGE_MEASUREMENT_MODEL_KINDS,
  type EntryConfigType,
  type EntryConfigUnion,
  type GradingDimension,
  type ImageCaptureRequirements,
  type ImageMeasurementModelOptions,
  type ObjectDetectionDefaultOptions,
} from "@/types/entryConfig";
import {
  convertDraftsToGrading,
  gradingToDrafts,
  validateGradingBuckets,
  type GradingBucketDraft,
} from "@/utils/gradingUtils";
import { useDirtySnapshot } from "@/hooks/useDirtySnapshot";
import { Archive, X, ChevronRight, Sparkles } from "lucide-react";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Controller, useForm, type UseFormRegisterReturn } from "react-hook-form";

interface AddTraitFormProps {
  onSubmit: (config: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">) => void;
  onCancel: () => void;
  initialTrait?: EntryConfigUnion;
  onDelete?: () => void;
  showDelete?: boolean;
  existingNames?: string[];
  /** Reports whether the form has unsaved edits, so the parent dialog can guard against accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

const DEFAULT_IMAGE_CAPTURE_DISTANCE_RANGE = [30, 120] as const;
const IMAGE_CAPTURE_DISTANCE_LIMITS = { min: 30, max: 300 };
const IMAGE_CAPTURE_SLIDER_STEP = 5;
const DEFAULT_IMAGE_CAPTURE_PITCH_RANGE = [-5, 10] as const;
const IMAGE_MEASUREMENT_PITCH_LIMITS = { min: -10, max: 180 };
const STAND_COUNT_PITCH_LIMITS = { min: -5, max: 65 };
// Leaf area index keeps its own pitch range but now shares the standard distance limits/sliders.
const LEAF_AREA_INDEX_PITCH_LIMITS = { min: -90, max: 90 };
const LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE = [50, 120] as const;
const LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE = [-5, 10] as const;
const RANGE_UPDATE_OPTIONS = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
} as const;
const RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: false,
} as const;

const getConfidenceThresholdFormValue = (modelOpts?: ImageMeasurementModelOptions) =>
  modelOpts && "confidenceThreshold" in modelOpts && modelOpts.confidenceThreshold != null
    ? (modelOpts.confidenceThreshold * 100).toString()
    : "30";

const formatRangeValue = (value: number, unit: string) => `${Math.round(value)}${unit}`;

const parseNumericInput = (value?: string) => {
  if (value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const hasFormValue = (value: unknown) => value !== undefined && value !== "";

const stringifyNumericValue = (value: number | null | undefined) => {
  if (value === undefined || value === null) {
    return "";
  }

  return value.toString();
};

const getExpandedLimits = (
  limits: { min: number; max: number },
  ...values: Array<string | undefined>
) => {
  let nextMin = limits.min;
  let nextMax = limits.max;

  values.forEach((value) => {
    const parsedValue = parseNumericInput(value);
    if (parsedValue === null) {
      return;
    }

    nextMin = Math.min(nextMin, parsedValue);
    nextMax = Math.max(nextMax, parsedValue);
  });

  return { min: nextMin, max: nextMax };
};

type ImageCaptureFormFields = {
  maxDistanceMeters?: string;
  minDistanceMeters?: string;
  maxPitchAngleDegrees?: string;
  minPitchAngleDegrees?: string;
};

const buildImageCaptureRequirements = ({
  maxDistanceMeters,
  minDistanceMeters,
  maxPitchAngleDegrees,
  minPitchAngleDegrees,
}: ImageCaptureFormFields): ImageCaptureRequirements | undefined => {
  const imageCaptureRequirements: ImageCaptureRequirements = {
    ...(hasFormValue(maxDistanceMeters) && { maxDistanceMeters: Number(maxDistanceMeters) / 100 }),
    ...(hasFormValue(minDistanceMeters) && { minDistanceMeters: Number(minDistanceMeters) / 100 }),
    ...(hasFormValue(maxPitchAngleDegrees) && { maxPitchAngleDegrees: Number(maxPitchAngleDegrees) }),
    ...(hasFormValue(minPitchAngleDegrees) && { minPitchAngleDegrees: Number(minPitchAngleDegrees) }),
  };

  return Object.keys(imageCaptureRequirements).length > 0 ? imageCaptureRequirements : undefined;
};

type ObjectDetectionDefaultFormFields = {
  maxLengthMeters?: string;
  minLengthMeters?: string;
};

const buildObjectDetectionDefaultOptions = ({
  maxLengthMeters,
  minLengthMeters,
}: ObjectDetectionDefaultFormFields): ObjectDetectionDefaultOptions | undefined => {
  const objectDetectionDefaultOptions: ObjectDetectionDefaultOptions = {
    ...(hasFormValue(maxLengthMeters) && { maxObjectLengthMeters: Number(maxLengthMeters) / 100 }),
    ...(hasFormValue(minLengthMeters) && { minObjectLengthMeters: Number(minLengthMeters) / 100 }),
  };

  return Object.keys(objectDetectionDefaultOptions).length > 0 ? objectDetectionDefaultOptions : undefined;
};

const getInitialImageCaptureRequirements = (trait?: EntryConfigUnion): ImageCaptureRequirements | undefined => {
  if (!trait) {
    return undefined;
  }

  if (trait.type === "imageMeasurement") {
    return trait.imageMeasurementConfig?.imageCaptureRequirements;
  }

  if (trait.type === "standCountMeasurement") {
    return trait.standCountMeasurementConfig?.imgMeasureConfig?.imageCaptureRequirements;
  }

  if (trait.type === "leafAreaIndex") {
    return trait.leafAreaIndexConfig?.imageCaptureRequirements;
  }

  if (trait.type === "photo") {
    return trait.photoConfig?.imageCaptureRequirements;
  }

  return undefined;
};

/**
 * Maps stored capture requirements onto the shared distance/pitch form fields.
 * Distance is stored in meters but edited in cm; unset bounds stay blank ("--"). Each present
 * distance bound is clamped into the standard range so a legacy value outside the current limits
 * (e.g. saved before the max was lowered to 300cm) loads as the nearest in-range value and stays
 * saveable; if clamping collapses the range (max <= min) the optional max is dropped.
 */
const captureRequirementsToFormValues = (requirements?: ImageCaptureRequirements) => {
  const minCm = requirements?.minDistanceMeters ? clampToStandardDistanceCm(requirements.minDistanceMeters * 100) : null;
  let maxCm = requirements?.maxDistanceMeters ? clampToStandardDistanceCm(requirements.maxDistanceMeters * 100) : null;
  if (minCm != null && maxCm != null && maxCm <= minCm) {
    maxCm = null;
  }
  return {
    minDistanceMeters: minCm != null ? minCm.toString() : "",
    maxDistanceMeters: maxCm != null ? maxCm.toString() : "",
    minPitchAngleDegrees: stringifyNumericValue(requirements?.minPitchAngleDegrees),
    maxPitchAngleDegrees: stringifyNumericValue(requirements?.maxPitchAngleDegrees),
  };
};

/**
 * Clamps a distance (cm) into the standard capture-distance slider limits. Used when hydrating LAI
 * traits, whose distance range narrowed from 10-200cm to the standard 30-500cm — a legacy sub-30cm
 * value is raised to 30cm so the trait stays editable/saveable (the slider validators reject <30cm).
 */
const clampToStandardDistanceCm = (cm: number) =>
  Math.min(IMAGE_CAPTURE_DISTANCE_LIMITS.max, Math.max(IMAGE_CAPTURE_DISTANCE_LIMITS.min, cm));

/**
 * Hydrates an LAI trait's distance bounds (cm form strings) from stored requirements, clamping each
 * legacy bound into the standard 30-500cm range AS A PAIR: if clamping collapses the range (max <= min,
 * e.g. a legacy 10-20cm range both clamping to 30), restore LAI's valid 50/120cm default range so the
 * trait stays saveable rather than tripping the `max <= min` validator.
 */
const laiDistanceFormValues = (requirements?: ImageCaptureRequirements) => {
  const minCm = requirements?.minDistanceMeters
    ? clampToStandardDistanceCm(requirements.minDistanceMeters * 100)
    : LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[0];
  const maxCm = requirements?.maxDistanceMeters
    ? clampToStandardDistanceCm(requirements.maxDistanceMeters * 100)
    : LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[1];
  if (maxCm <= minCm) {
    return {
      minDistanceMeters: LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[0].toString(),
      maxDistanceMeters: LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[1].toString(),
    };
  }
  return { minDistanceMeters: minCm.toString(), maxDistanceMeters: maxCm.toString() };
};

/** Trait types that share the capture-requirement distance/pitch form fields. */
const CAPTURE_REQUIREMENT_TYPES = new Set<EntryConfigType>([
  "photo",
  "imageMeasurement",
  "standCountMeasurement",
  "leafAreaIndex",
]);

/**
 * Per-trait-type starting values for the shared capture-requirement form fields (cm / degrees as strings).
 * Because photo/imageMeasurement/standCount/leafAreaIndex all bind to the SAME form fields, the seeding
 * effect re-applies these on every type change so one type's values can't leak into another, and the
 * "Reset to Default" buttons restore the correct per-type defaults.
 * - leafAreaIndex: its full 50/120cm + -5/+10° range.
 * - photo: fully optional — nothing pre-seeded (matches the "leave a bound unset" copy).
 * - imageMeasurement / standCountMeasurement: a saved 30cm minimum, optional upper/pitch bounds.
 */
const captureRequirementDefaultsForType = (type?: EntryConfigType) => {
  if (type === "leafAreaIndex") {
    return {
      minDistanceMeters: LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[0].toString(),
      maxDistanceMeters: LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[1].toString(),
      minPitchAngleDegrees: LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE[0].toString(),
      maxPitchAngleDegrees: LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE[1].toString(),
    };
  }
  if (type === "photo") {
    return { minDistanceMeters: "", maxDistanceMeters: "", minPitchAngleDegrees: "", maxPitchAngleDegrees: "" };
  }
  return {
    minDistanceMeters: DEFAULT_IMAGE_CAPTURE_DISTANCE_RANGE[0].toString(),
    maxDistanceMeters: "",
    minPitchAngleDegrees: "",
    maxPitchAngleDegrees: "",
  };
};

const getSliderRangeValues = (
  minValue: string | undefined,
  maxValue: string | undefined,
  defaults: readonly [number, number],
  limits: { min: number; max: number },
  minimumGap = 1
): [number, number] => {
  const parsedMin = parseNumericInput(minValue);
  const parsedMax = parseNumericInput(maxValue);
  const resolvedGap = Math.max(1, minimumGap);

  let resolvedMin = parsedMin ?? defaults[0];
  let resolvedMax = parsedMax ?? defaults[1];

  resolvedMin = clampValue(resolvedMin, limits.min, limits.max);
  resolvedMax = clampValue(resolvedMax, limits.min, limits.max);

  if (resolvedMin >= resolvedMax) {
    if (parsedMin === null && parsedMax !== null) {
      resolvedMin = Math.max(limits.min, resolvedMax - resolvedGap);
    } else if (parsedMin !== null && parsedMax === null) {
      resolvedMax = Math.min(limits.max, resolvedMin + resolvedGap);
    } else if (resolvedMin < limits.max) {
      resolvedMax = Math.min(limits.max, resolvedMin + resolvedGap);
    } else {
      resolvedMin = Math.max(limits.min, resolvedMax - resolvedGap);
    }
  }

  return [resolvedMin, resolvedMax];
};

interface DualThumbRangeFieldProps {
  label: string;
  description: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  values: [number, number];
  onValueChange: (values: [number, number]) => void;
  disabled?: boolean;
  suggested?: boolean;
  minStepsBetweenThumbs?: number;
  thumbDisplayValues?: [string, string];
  actions?: React.ReactNode;
  errorMessages?: string[];
  suggestedHint?: string;
}

const DualThumbRangeField: React.FC<DualThumbRangeFieldProps> = ({
  label,
  description,
  min,
  max,
  step,
  unit,
  values,
  onValueChange,
  disabled = false,
  suggested = false,
  minStepsBetweenThumbs = 0,
  thumbDisplayValues,
  actions,
  errorMessages = [],
  suggestedHint,
}) => {
  const dedupedErrors = [...new Set(errorMessages.filter(Boolean))];

  return (
    <div className={cn("space-y-3 transition-opacity", (disabled || suggested) && "opacity-70")}>
      <div className="space-y-1">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
        {suggested && suggestedHint && <p className="text-xs text-muted-foreground">{suggestedHint}</p>}
      </div>

      <div className="pt-8">
        <Slider
          min={min}
          max={max}
          step={step}
          minStepsBetweenThumbs={minStepsBetweenThumbs}
          value={values}
          onValueChange={(nextValues) => onValueChange([Math.round(nextValues[0]), Math.round(nextValues[1])])}
          thumbLabels={[`${label} minimum`, `${label} maximum`]}
          thumbContents={thumbDisplayValues ?? [formatRangeValue(values[0], unit), formatRangeValue(values[1], unit)]}
          className="w-full"
          disabled={disabled}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatRangeValue(min, unit)}</span>
        <span>{formatRangeValue(max, unit)}</span>
      </div>

      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}

      {dedupedErrors.map((message) => (
        <p
          key={message}
          className="text-sm text-destructive">
          {message}
        </p>
      ))}
    </div>
  );
};

interface ObjectDetectionConfigSectionProps {
  objectNames: string[];
  newObjectName: string;
  objectNamesError: string | null;
  confidenceThreshold: string;
  advancedOptionsOpen: boolean;
  isSystemTrait: boolean;
  newObjectNameInputProps: UseFormRegisterReturn;
  minLengthInputProps: UseFormRegisterReturn;
  maxLengthInputProps: UseFormRegisterReturn;
  minLengthError?: string;
  maxLengthError?: string;
  onAddObjectName: () => void;
  onRemoveObjectName: (name: string) => void;
  onConfidenceChange: (value: string) => void;
  onAdvancedOptionsOpenChange: (open: boolean) => void;
  showAdvancedOptions?: boolean;
}

const ObjectDetectionAdvancedOptions: React.FC<{
  confidenceThreshold: string;
  advancedOptionsOpen: boolean;
  isSystemTrait: boolean;
  minLengthInputProps: UseFormRegisterReturn;
  maxLengthInputProps: UseFormRegisterReturn;
  minLengthError?: string;
  maxLengthError?: string;
  onConfidenceChange: (value: string) => void;
  onAdvancedOptionsOpenChange: (open: boolean) => void;
}> = ({
  confidenceThreshold,
  advancedOptionsOpen,
  isSystemTrait,
  minLengthInputProps,
  maxLengthInputProps,
  minLengthError,
  maxLengthError,
  onConfidenceChange,
  onAdvancedOptionsOpenChange,
}) => (
  <Collapsible
    open={advancedOptionsOpen}
    onOpenChange={onAdvancedOptionsOpenChange}
    data-testid="object-detection-advanced-options"
    className="border-t border-border pt-4">
    <CollapsibleTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        className="flex items-center gap-2 w-full justify-start p-0 hover:bg-transparent">
        <ChevronRight className={cn("h-4 w-4 transition-transform", advancedOptionsOpen && "rotate-90")} />
        <span className="text-sm font-medium">Advanced Options (optional)</span>
      </Button>
    </CollapsibleTrigger>
    <CollapsibleContent className="space-y-4 mt-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="confidenceThreshold">Confidence Threshold</Label>
          <span className="text-sm text-muted-foreground">{confidenceThreshold}%</span>
        </div>
        <Slider
          id="confidenceThreshold"
          min={0}
          max={100}
          step={1}
          value={[Number(confidenceThreshold)]}
          onValueChange={(values) => onConfidenceChange(values[0].toString())}
          className="w-full"
          disabled={isSystemTrait}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="minLengthMeters">Min Length (cm)</Label>
          <Input
            id="minLengthMeters"
            type="number"
            step="0.01"
            {...minLengthInputProps}
            placeholder="Min length (cm)"
            className={cn(minLengthError && "border-destructive")}
            disabled={isSystemTrait}
          />
          {minLengthError && <p className="text-sm text-destructive">{minLengthError}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="maxLengthMeters">Max Length (cm)</Label>
          <Input
            id="maxLengthMeters"
            type="number"
            step="0.01"
            {...maxLengthInputProps}
            placeholder="Max length (cm)"
            className={cn(maxLengthError && "border-destructive")}
            disabled={isSystemTrait}
          />
          {maxLengthError && <p className="text-sm text-destructive">{maxLengthError}</p>}
        </div>
      </div>
    </CollapsibleContent>
  </Collapsible>
);

const ObjectDetectionConfigSection: React.FC<ObjectDetectionConfigSectionProps> = ({
  objectNames,
  newObjectName,
  objectNamesError,
  confidenceThreshold,
  advancedOptionsOpen,
  isSystemTrait,
  newObjectNameInputProps,
  minLengthInputProps,
  maxLengthInputProps,
  minLengthError,
  maxLengthError,
  onAddObjectName,
  onRemoveObjectName,
  onConfidenceChange,
  onAdvancedOptionsOpenChange,
  showAdvancedOptions = true,
}) => (
  <div>
    <h4 className="font-medium text-sm mb-2">Object Detection Configuration</h4>
    <p className="text-sm text-muted-foreground mb-4">Configure how the AI model detects objects</p>

    <div className="space-y-4">
      <div className="space-y-2">
        <Label>What to measure</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {objectNames.map((name, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="px-2 py-1">
              {name}
              {!isSystemTrait && (
                <button
                  type="button"
                  onClick={() => onRemoveObjectName(name)}
                  className="ml-1 text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
        {objectNamesError && <p className="text-sm text-destructive mb-2">{objectNamesError}</p>}
        <div className="flex space-x-2">
          <Input
            {...newObjectNameInputProps}
            placeholder="Add object name"
            className="flex-1"
            disabled={isSystemTrait}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (newObjectName.trim()) {
                  onAddObjectName();
                }
              } else if (e.key === "Tab" && newObjectName.trim()) {
                e.preventDefault();
                onAddObjectName();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={onAddObjectName}
            disabled={!newObjectName.trim() || isSystemTrait}>
            Add
          </Button>
        </div>
      </div>

      {showAdvancedOptions && (
        <ObjectDetectionAdvancedOptions
          confidenceThreshold={confidenceThreshold}
          advancedOptionsOpen={advancedOptionsOpen}
          isSystemTrait={isSystemTrait}
          minLengthInputProps={minLengthInputProps}
          maxLengthInputProps={maxLengthInputProps}
          minLengthError={minLengthError}
          maxLengthError={maxLengthError}
          onConfidenceChange={onConfidenceChange}
          onAdvancedOptionsOpenChange={onAdvancedOptionsOpenChange}
        />
      )}
    </div>
  </div>
);

const AddTraitForm: React.FC<AddTraitFormProps> = ({
  onSubmit,
  onCancel,
  initialTrait,
  onDelete,
  showDelete,
  existingNames,
  onDirtyChange,
}) => {
  // Check if this is a system trait (non-editable)
  const isSystemTrait = initialTrait?.isSystem === true;

  // Helper function to get initial values from existing trait
  const getInitialValues = () => {
    const defaultValues = {
      name: "",
      description: "",
      alwaysShowDescription: false,
      type: "text" as EntryConfigType,
      isArchived: false,
      metadata: {},
      // Number config
      wholeNumbersOnly: true,
      min: "",
      max: "",
      // Category config
      choices: [] as string[],
      allowMultipleSelections: false,
      newChoice: "",
      // Date config
      dateMin: "",
      dateMax: "",
      // Image measurement config
      objectNames: [] as string[],
      newObjectName: "",
      maxLengthMeters: "",
      minLengthMeters: "",
      maxDistanceMeters: "",
      minDistanceMeters: DEFAULT_IMAGE_CAPTURE_DISTANCE_RANGE[0].toString(),
      maxPitchAngleDegrees: "",
      minPitchAngleDegrees: "",
      confidenceThreshold: "30",
      // Stand count measurement config
      standCountModelMode: "openSet" as "openSet" | "none",
      boxLengthMeters: "50",
      boxWidthMeters: "50",
      // Ruler config
      rulerMinLengthMeters: "",
      rulerMaxLengthMeters: "",
      // Timer config
      timerStartStopOnBarcodeScan: false,
      // Leaf area index config (distance/pitch now use the shared capture-requirement fields)
      leafAreaUseFixedBoxSize: false,
      leafAreaDefaultSensitivity: "50",
      leafAreaBoxLengthMeters: "50",
      leafAreaBoxWidthMeters: "50",
      // Row planted stand count config - Model Settings
      rowPlantedModelKind: "genericCrop" as "genericCrop" | "openSet",
      rowPlantedDetectionPrompt: "",
      rowPlantedModelSensitivity: "70",
      // Walk Settings
      rowPlantedNumPlantLines: "",
      rowPlantedWalkDistanceMeters: "",
      rowPlantedIsWalkDistanceRequired: false,
      // Plant Spacing & Size (display in cm, store in meters)
      rowPlantedMinSpacingCm: "",
      rowPlantedMinPlantDiameterCm: "",
      rowPlantedMaxPlantDiameterCm: "",
      // Detection Distance (display in cm, store in meters)
      rowPlantedMinDetectionDistanceCm: "",
      rowPlantedMaxDetectionDistanceCm: "",
      // Measurement Criteria
      rowPlantedMinMeasurementAngle: "",
      rowPlantedMaxMeasurementAngle: "",
      rowPlantedMaxMeasurementDistanceCm: "",
      // Size Filtering (percentages)
      rowPlantedMaxSizePercent: "",
      rowPlantedMinSizePercent: "",
    };

    if (!initialTrait) return defaultValues;

    // Map existing trait to form values
    const values = {
      ...defaultValues,
      name: initialTrait.name,
      description: initialTrait.description,
      alwaysShowDescription: initialTrait.alwaysShowDescription ?? false,
      type: initialTrait.type,
      isArchived: initialTrait.isArchived,
      metadata: initialTrait.metadata,
    };

    // Type-specific mappings
    switch (initialTrait.type) {
      case "number":
        return {
          ...values,
          min: initialTrait.numberConfig?.min?.toString() ?? "",
          max: initialTrait.numberConfig?.max?.toString() ?? "",
          wholeNumbersOnly: initialTrait.numberConfig?.wholeNumbersOnly ?? false,
        };
      case "category":
        return {
          ...values,
          choices: initialTrait.categoryConfig?.choices ?? [],
          allowMultipleSelections: initialTrait.categoryConfig?.allowMultipleSelections ?? false,
        };
      case "date":
        return {
          ...values,
          dateMin: initialTrait.dateConfig?.min ?? "",
          dateMax: initialTrait.dateConfig?.max ?? "",
        };
      case "imageMeasurement": {
        const modelOpts = initialTrait.imageMeasurementConfig?.imageMeasurementModelOptions;
        const captureReqs = initialTrait.imageMeasurementConfig?.imageCaptureRequirements;
        const defaultOpts = initialTrait.imageMeasurementConfig?.objectDetectionDefaultOptions;

        return {
          ...values,
          objectNames:
            modelOpts?.kind === IMAGE_MEASUREMENT_MODEL_KINDS.OPEN_SET_OBJECT_DETECTOR
              ? modelOpts.prompts
              : [],
          minLengthMeters: defaultOpts?.minObjectLengthMeters
            ? (defaultOpts.minObjectLengthMeters * 100).toString()
            : "",
          maxLengthMeters: defaultOpts?.maxObjectLengthMeters
            ? (defaultOpts.maxObjectLengthMeters * 100).toString()
            : "",
          ...captureRequirementsToFormValues(captureReqs),
          confidenceThreshold: getConfidenceThresholdFormValue(modelOpts),
        };
      }
      case "ruler":
        return {
          ...values,
          rulerMinLengthMeters: initialTrait.rulerConfig?.minLengthMeters?.toString() ?? "",
          rulerMaxLengthMeters: initialTrait.rulerConfig?.maxLengthMeters?.toString() ?? "",
        };
      case "timer":
        return {
          ...values,
          timerStartStopOnBarcodeScan:
            initialTrait.timerConfig?.startStopOnBarcodeScan ?? false,
        };
      case "leafAreaIndex": {
        const captureReqs = initialTrait.leafAreaIndexConfig?.imageCaptureRequirements;
        return {
          ...values,
          leafAreaUseFixedBoxSize: initialTrait.leafAreaIndexConfig?.useFixedBoxSize ?? false,
          // Zero is a valid sensitivity. Box dimensions use truthiness below because zero is invalid.
          leafAreaDefaultSensitivity: initialTrait.leafAreaIndexConfig?.defaultSensitivity != null
            ? (initialTrait.leafAreaIndexConfig.defaultSensitivity * 100).toString()
            : "50",
          leafAreaBoxLengthMeters: initialTrait.leafAreaIndexConfig?.boxLengthMeters
            ? (initialTrait.leafAreaIndexConfig.boxLengthMeters * 100).toString()
            : "50",
          leafAreaBoxWidthMeters: initialTrait.leafAreaIndexConfig?.boxWidthMeters
            ? (initialTrait.leafAreaIndexConfig.boxWidthMeters * 100).toString()
            : "50",
          // LAI shares the standard capture-requirement sliders; fall back to LAI defaults when unset,
          // clamping any legacy sub-30cm distance into the new range as a pair (see laiDistanceFormValues).
          ...laiDistanceFormValues(captureReqs),
          minPitchAngleDegrees:
            captureReqs?.minPitchAngleDegrees != null
              ? captureReqs.minPitchAngleDegrees.toString()
              : LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE[0].toString(),
          maxPitchAngleDegrees:
            captureReqs?.maxPitchAngleDegrees != null
              ? captureReqs.maxPitchAngleDegrees.toString()
              : LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE[1].toString(),
        };
      }
      case "photo": {
        // The generic photo trait reuses the shared capture-requirement sliders; unset bounds stay "--".
        return {
          ...values,
          ...captureRequirementsToFormValues(initialTrait.photoConfig?.imageCaptureRequirements),
        };
      }
      case "standCountMeasurement": {
        const modelOpts = initialTrait.standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions;
        const captureReqs = initialTrait.standCountMeasurementConfig?.imgMeasureConfig?.imageCaptureRequirements;
        const defaultOpts = initialTrait.standCountMeasurementConfig?.imgMeasureConfig?.objectDetectionDefaultOptions;

        return {
          ...values,
          standCountModelMode:
            initialTrait.standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions?.kind === "none"
              ? "none"
              : "openSet",
          boxLengthMeters: initialTrait.standCountMeasurementConfig?.boxLengthMeters
            ? (initialTrait.standCountMeasurementConfig.boxLengthMeters * 100).toString()
            : "",
          boxWidthMeters: initialTrait.standCountMeasurementConfig?.boxWidthMeters
            ? (initialTrait.standCountMeasurementConfig.boxWidthMeters * 100).toString()
            : "",
          objectNames: modelOpts?.kind === "openSetObjectDetector" ? modelOpts.prompts : [],
          minLengthMeters: defaultOpts?.minObjectLengthMeters
            ? (defaultOpts.minObjectLengthMeters * 100).toString()
            : "",
          maxLengthMeters: defaultOpts?.maxObjectLengthMeters
            ? (defaultOpts.maxObjectLengthMeters * 100).toString()
            : "",
          ...captureRequirementsToFormValues(captureReqs),
          confidenceThreshold:
            modelOpts?.kind === "openSetObjectDetector" && modelOpts.confidenceThreshold
              ? (modelOpts.confidenceThreshold * 100).toString()
              : "30",
        };
      }
      case "rowPlantedStandCount": {
        const config = initialTrait.rowPlantedStandCountConfig;
        const modelOpts = config?.modelOptions;
        const isOpenSet = modelOpts?.kind === "openSet";

        return {
          ...values,
          // Model Settings
          rowPlantedModelKind: isOpenSet ? "openSet" : "genericCrop",
          rowPlantedDetectionPrompt: isOpenSet ? ((modelOpts as any).detectionPrompt ?? "") : "",
          rowPlantedModelSensitivity:
            isOpenSet && (modelOpts as any).modelSensitivity != null
              ? ((modelOpts as any).modelSensitivity * 100).toString()
              : "70",
          // Walk Settings (FIX: use correct field names)
          rowPlantedNumPlantLines: config?.expectedPlantLineCount?.toString() ?? "",
          rowPlantedWalkDistanceMeters: config?.walkDistanceMetersTarget?.toString() ?? "",
          rowPlantedIsWalkDistanceRequired: config?.isWalkDistanceRequired ?? false,
          // Plant Spacing & Size (convert meters to cm for display)
          rowPlantedMinSpacingCm: config?.minPlantSpacingMeters ? (config.minPlantSpacingMeters * 100).toString() : "",
          rowPlantedMinPlantDiameterCm: config?.minPlantDiameterMeters
            ? (config.minPlantDiameterMeters * 100).toString()
            : "",
          rowPlantedMaxPlantDiameterCm: config?.maxPlantDiameterMeters
            ? (config.maxPlantDiameterMeters * 100).toString()
            : "",
          // Detection Distance (convert meters to cm)
          rowPlantedMinDetectionDistanceCm: config?.minDetectionDistanceMeters
            ? (config.minDetectionDistanceMeters * 100).toString()
            : "",
          rowPlantedMaxDetectionDistanceCm: config?.maxDetectionDistanceMeters
            ? (config.maxDetectionDistanceMeters * 100).toString()
            : "",
          // Measurement Criteria
          rowPlantedMinMeasurementAngle: config?.minMeasurementAngleDegrees?.toString() ?? "",
          rowPlantedMaxMeasurementAngle: config?.maxMeasurementAngleDegrees?.toString() ?? "",
          rowPlantedMaxMeasurementDistanceCm: config?.maxMeasurementDistanceMeters
            ? (config.maxMeasurementDistanceMeters * 100).toString()
            : "",
          // Size Filtering (already in percent)
          rowPlantedMaxSizePercent: config?.maxSizePercentOfImage?.toString() ?? "",
          rowPlantedMinSizePercent: config?.minSizePercentOfImage?.toString() ?? "",
        };
      }
      default:
        return values;
    }
  };

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({
    defaultValues: getInitialValues(),
    mode: "onChange",
  });

  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
  const [rowPlantedAdvancedOpen, setRowPlantedAdvancedOpen] = useState(false);
  const [objectNamesError, setObjectNamesError] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<"1-to-5" | "1-to-9" | null>(null);

  const initialGrading =
    initialTrait?.type === "imageMeasurement"
      ? initialTrait.imageMeasurementConfig?.grading
      : undefined;
  const [gradingDimension, setGradingDimension] = useState<GradingDimension>(
    initialGrading?.dimension ?? "width"
  );
  const [gradingDrafts, setGradingDrafts] = useState<GradingBucketDraft[]>(
    initialGrading ? gradingToDrafts(initialGrading) : []
  );
  const [gradingSectionOpen, setGradingSectionOpen] = useState<boolean>(
    !!initialGrading && initialGrading.buckets.length > 0
  );
  // The editor holds out-of-order typed thresholds locally (it doesn't emit them),
  // so gradingValidation alone can't see them. This mirrors the editor's own
  // validity so we don't save while a typed threshold is still out of order.
  const [gradingEditorValid, setGradingEditorValid] = useState(true);
  const hasInitialGrading = !!initialGrading && initialGrading.buckets.length > 0;
  // Whether grading is "active" (the user actually configured it) vs the untouched
  // dulled default seed. Driven by the editor's onActiveChange; starts active only when
  // editing a trait that already had grading. We attach `grading` on save iff active,
  // so merely opening the optional section and saving doesn't add default grades.
  const [gradingEditorActive, setGradingEditorActive] = useState(hasInitialGrading);
  const gradingValidation = useMemo(
    () => validateGradingBuckets(gradingDrafts),
    [gradingDrafts]
  );

  // Grading (image-measurement size grades) lives in local state, not react-hook-form,
  // so fold it into the dirty signal the wrapping dialog reads. Mirror exactly what
  // would be SAVED — the configured buckets only when grading is active — so editing
  // or clearing grades marks the form dirty, while merely opening the optional
  // (default-seeded) grading section does not. Chip and number-preset edits use
  // setValue({ shouldDirty: true }), so RHF's own isDirty already covers those.
  const gradingSignature =
    gradingEditorActive && gradingDrafts.length > 0
      ? { dimension: gradingDimension, drafts: gradingDrafts }
      : null;
  const gradingDirty = useDirtySnapshot({ gradingSignature });

  // Surface unsaved-edit state so the wrapping dialog can confirm before discarding.
  useEffect(() => {
    onDirtyChange?.(isDirty || gradingDirty);
  }, [isDirty, gradingDirty, onDirtyChange]);

  const configType = watch("type");
  const choices = watch("choices");
  const newChoice = watch("newChoice");
  const objectNames = watch("objectNames");
  const newObjectName = watch("newObjectName");
  const confidenceThreshold = watch("confidenceThreshold");
  const standCountModelMode = watch("standCountModelMode");
  const boxLengthMeters = watch("boxLengthMeters");
  const boxWidthMeters = watch("boxWidthMeters");
  const minDistanceMeters = watch("minDistanceMeters");
  const maxDistanceMeters = watch("maxDistanceMeters");
  const minPitchAngleDegrees = watch("minPitchAngleDegrees");
  const maxPitchAngleDegrees = watch("maxPitchAngleDegrees");
  const leafAreaDefaultSensitivity = watch("leafAreaDefaultSensitivity");
  const leafAreaUseFixedBoxSize = watch("leafAreaUseFixedBoxSize");
  const rowPlantedModelKind = watch("rowPlantedModelKind");
  const rowPlantedModelSensitivity = watch("rowPlantedModelSensitivity");
  const rowPlantedIsWalkDistanceRequired = watch("rowPlantedIsWalkDistanceRequired");
  const watchedName = watch("name");
  const watchedMin = watch("min");
  const watchedMax = watch("max");
  const standCountPreviewTimeoutRef = useRef<number | null>(null);
  const [standCountPreviewPaused, setStandCountPreviewPaused] = useState(false);

  // Duplicate name check (case-insensitive), memoized to avoid recomputing on unrelated field changes
  const hasDuplicateName = useMemo(
    () => (existingNames ? isDuplicateName(watchedName, existingNames, initialTrait?.name) : false),
    [existingNames, watchedName, initialTrait?.name]
  );

  const initialImageCaptureRequirements = useMemo(() => getInitialImageCaptureRequirements(initialTrait), [initialTrait]);
  const currentPitchLimits =
    configType === "standCountMeasurement"
      ? STAND_COUNT_PITCH_LIMITS
      : configType === "leafAreaIndex"
        ? LEAF_AREA_INDEX_PITCH_LIMITS
        : IMAGE_MEASUREMENT_PITCH_LIMITS;
  const displayPitchLimits = useMemo(
    () => getExpandedLimits(currentPitchLimits, minPitchAngleDegrees, maxPitchAngleDegrees),
    [currentPitchLimits, maxPitchAngleDegrees, minPitchAngleDegrees]
  );

  const imageCaptureDistanceRangeValues = useMemo(
    () =>
      getSliderRangeValues(
        minDistanceMeters,
        maxDistanceMeters,
        DEFAULT_IMAGE_CAPTURE_DISTANCE_RANGE,
        IMAGE_CAPTURE_DISTANCE_LIMITS,
        IMAGE_CAPTURE_SLIDER_STEP
      ),
    [maxDistanceMeters, minDistanceMeters]
  );

  const imageCapturePitchRangeValues = useMemo(
    () =>
      getSliderRangeValues(
        minPitchAngleDegrees,
        maxPitchAngleDegrees,
        DEFAULT_IMAGE_CAPTURE_PITCH_RANGE,
        displayPitchLimits,
        IMAGE_CAPTURE_SLIDER_STEP
      ),
    [displayPitchLimits, maxPitchAngleDegrees, minPitchAngleDegrees]
  );

  const hasMinDistanceCapture = parseNumericInput(minDistanceMeters) !== null;
  const hasMaxDistanceCapture = parseNumericInput(maxDistanceMeters) !== null;
  const hasMinPitchCapture = parseNumericInput(minPitchAngleDegrees) !== null;
  const hasMaxPitchCapture = parseNumericInput(maxPitchAngleDegrees) !== null;
  const standCountBoxLongestSideCm = Math.max(parseNumericInput(boxLengthMeters) ?? 50, parseNumericInput(boxWidthMeters) ?? 50);
  const isPersistedLegacyPitchValue = (
    field: "minPitchAngleDegrees" | "maxPitchAngleDegrees",
    value: number,
    limits: { min: number; max: number }
  ) => {
    if (!initialImageCaptureRequirements || initialTrait?.type !== configType || dirtyFields[field]) {
      return false;
    }

    const initialValue = initialImageCaptureRequirements[field];
    return typeof initialValue === "number" && initialValue === value && (value < limits.min || value > limits.max);
  };

  const newObjectNameField = register("newObjectName");
  const minLengthMetersField = register("minLengthMeters", {
    validate: (value) => {
      if (!value) return true;
      const num = Number(value);
      if (num <= 0) {
        return "Min length must be greater than 0";
      }
      const maxLen = watch("maxLengthMeters");
      if (maxLen && num >= Number(maxLen)) {
        return "Min length must be less than max length";
      }
      return true;
    },
  });
  const maxLengthMetersField = register("maxLengthMeters", {
    validate: (value) => {
      if (!value) return true;
      const num = Number(value);
      if (num <= 0) {
        return "Max length must be greater than 0";
      }
      if (num > 500) {
        return "Maximum object length is 5m (500cm)";
      }
      const minLen = watch("minLengthMeters");
      if (minLen && num <= Number(minLen)) {
        return "Max length must be greater than min length";
      }
      return true;
    },
  });

  const pingStandCountPreviewPause = () => {
    if (configType !== "standCountMeasurement") {
      return;
    }

    setStandCountPreviewPaused(true);

    if (standCountPreviewTimeoutRef.current) {
      window.clearTimeout(standCountPreviewTimeoutRef.current);
    }

    standCountPreviewTimeoutRef.current = window.setTimeout(() => {
      setStandCountPreviewPaused(false);
      standCountPreviewTimeoutRef.current = null;
    }, 700);
  };

  // Clear error when objectNames changes or type changes
  useEffect(() => {
    if (
      (configType !== "imageMeasurement" && configType !== "standCountMeasurement") ||
      objectNames.length > 0 ||
      standCountModelMode === "none"
    ) {
      setObjectNamesError(null);
    }
  }, [objectNames, configType, standCountModelMode]);

  useEffect(() => {
    return () => {
      if (standCountPreviewTimeoutRef.current) {
        window.clearTimeout(standCountPreviewTimeoutRef.current);
      }
    };
  }, []);

  // Re-seed the SHARED capture-requirement fields with the per-type defaults whenever the trait type
  // CHANGES. photo/imageMeasurement/standCount/leafAreaIndex all bind to the same fields, so re-seeding
  // on every type change stops one type's values from leaking into another (e.g. selecting Leaf Area
  // Index then switching to Photo must not carry LAI's 50/120cm into the photo trait). The ref is
  // seeded with the edited trait's type so an edit session preserves its hydrated values on mount and
  // only re-seeds once the user actually switches the type (e.g. converting a text trait to Photo).
  const seededCaptureTypeRef = useRef<EntryConfigType | null>(initialTrait?.type ?? null);
  useEffect(() => {
    if (seededCaptureTypeRef.current === configType) {
      return;
    }
    seededCaptureTypeRef.current = configType;
    if (!CAPTURE_REQUIREMENT_TYPES.has(configType)) {
      return;
    }
    const defaults = captureRequirementDefaultsForType(configType);
    setValue("minDistanceMeters", defaults.minDistanceMeters);
    setValue("maxDistanceMeters", defaults.maxDistanceMeters);
    setValue("minPitchAngleDegrees", defaults.minPitchAngleDegrees);
    setValue("maxPitchAngleDegrees", defaults.maxPitchAngleDegrees);
  }, [configType, setValue]);

  // Deselect preset if user manually changes min/max to non-preset values
  useEffect(() => {
    if (selectedPreset === null) return;

    const matchesPreset =
      (selectedPreset === "1-to-5" && watchedMin === "1" && watchedMax === "5") ||
      (selectedPreset === "1-to-9" && watchedMin === "1" && watchedMax === "9");

    if (!matchesPreset) {
      setSelectedPreset(null);
    }
  }, [watchedMin, watchedMax, selectedPreset]);

  const applyNumberPreset = (preset: "1-to-5" | "1-to-9") => {
    // Toggle off if already selected
    if (selectedPreset === preset) {
      setValue("min", "", { shouldDirty: true });
      setValue("max", "", { shouldDirty: true });
      setSelectedPreset(null);
      return;
    }

    const max = preset === "1-to-5" ? "5" : "9";
    setValue("min", "1", { shouldDirty: true });
    setValue("max", max, { shouldDirty: true });
    setValue("wholeNumbersOnly", true, { shouldDirty: true });
    setSelectedPreset(preset);
  };

  const addChoice = () => {
    if (newChoice.trim() && !choices.includes(newChoice.trim())) {
      setValue("choices", [...choices, newChoice.trim()], { shouldDirty: true });
      setValue("newChoice", "");
    }
  };

  const removeChoice = (choice: string) => {
    setValue(
      "choices",
      choices.filter((c) => c !== choice),
      { shouldDirty: true }
    );
  };

  const addObjectName = () => {
    if (newObjectName.trim() && !objectNames.includes(newObjectName.trim())) {
      setValue("objectNames", [...objectNames, newObjectName.trim()], { shouldDirty: true });
      setValue("newObjectName", "");
      setObjectNamesError(null); // Clear error when user adds an object
    }
  };

  const removeObjectName = (name: string) => {
    setValue(
      "objectNames",
      objectNames.filter((n) => n !== name),
      { shouldDirty: true }
    );
  };

  const updateImageCaptureRange = (
    minField: "minDistanceMeters" | "minPitchAngleDegrees",
    maxField: "maxDistanceMeters" | "maxPitchAngleDegrees",
    values: [number, number],
    previousValues: [number, number]
  ) => {
    if (values[0] !== previousValues[0]) {
      setValue(minField, values[0].toString(), RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);
    }

    if (values[1] !== previousValues[1]) {
      setValue(maxField, values[1].toString(), RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);
    }

    void trigger([minField, maxField]);
  };

  const clearImageCaptureBound = (
    field: "minDistanceMeters" | "maxDistanceMeters" | "minPitchAngleDegrees" | "maxPitchAngleDegrees"
  ) => {
    setValue(field, "", RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);

    if (field === "minDistanceMeters" || field === "maxDistanceMeters") {
      void trigger(["minDistanceMeters", "maxDistanceMeters"]);
    } else {
      void trigger(["minPitchAngleDegrees", "maxPitchAngleDegrees"]);
    }
  };

  const resetImageCaptureRange = (rangeType: "distance" | "pitch") => {
    // Reset to the CURRENT trait type's defaults (e.g. LAI restores 50/120cm + -5/+10°, not the
    // generic image-measurement 30cm/unset).
    const defaults = captureRequirementDefaultsForType(configType);
    if (rangeType === "distance") {
      setValue("minDistanceMeters", defaults.minDistanceMeters, RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);
      setValue("maxDistanceMeters", defaults.maxDistanceMeters, RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);
      void trigger(["minDistanceMeters", "maxDistanceMeters"]);
      return;
    }

    setValue("minPitchAngleDegrees", defaults.minPitchAngleDegrees, RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);
    setValue("maxPitchAngleDegrees", defaults.maxPitchAngleDegrees, RANGE_UPDATE_WITHOUT_VALIDATION_OPTIONS);
    void trigger(["minPitchAngleDegrees", "maxPitchAngleDegrees"]);
  };

  const renderImageCaptureRequirementInputs = (pitchLimits: { min: number; max: number }) => (
    <>
      <input
        type="hidden"
        {...register("minDistanceMeters", {
          validate: (value) => {
            if (!value) return true;
            const num = Number(value);
            if (num < 30) {
              return "Measurements are not reliable below 30cm";
            }
            if (num > 300) {
              return "Maximum distance is 3m (300cm)";
            }
            const maxDist = watch("maxDistanceMeters");
            if (maxDist && num >= Number(maxDist)) {
              return "Min distance must be less than max distance";
            }
            return true;
          },
        })}
      />
      <input
        type="hidden"
        {...register("maxDistanceMeters", {
          validate: (value) => {
            if (!value) return true;
            const num = Number(value);
            if (num < 30) {
              return "Measurements are not reliable below 30cm";
            }
            if (num > 300) {
              return "Maximum distance is 3m (300cm)";
            }
            const minDist = watch("minDistanceMeters");
            if (minDist && num <= Number(minDist)) {
              return "Max distance must be greater than min distance";
            }
            return true;
          },
        })}
      />
      <input
        type="hidden"
        {...register("minPitchAngleDegrees", {
          validate: (value) => {
            if (!value && value !== "0") return true;
            const num = Number(value);
            if (num < pitchLimits.min || num > pitchLimits.max) {
              if (isPersistedLegacyPitchValue("minPitchAngleDegrees", num, pitchLimits)) {
                return true;
              }
              return `Must be between ${pitchLimits.min} and ${pitchLimits.max}`;
            }
            const maxPitch = watch("maxPitchAngleDegrees");
            if (maxPitch && num >= Number(maxPitch)) {
              return "Min pitch angle must be less than max pitch angle";
            }
            return true;
          },
        })}
      />
      <input
        type="hidden"
        {...register("maxPitchAngleDegrees", {
          validate: (value) => {
            if (!value && value !== "0") return true;
            const num = Number(value);
            if (num < pitchLimits.min || num > pitchLimits.max) {
              if (isPersistedLegacyPitchValue("maxPitchAngleDegrees", num, pitchLimits)) {
                return true;
              }
              return `Must be between ${pitchLimits.min} and ${pitchLimits.max}`;
            }
            const minPitch = watch("minPitchAngleDegrees");
            if (minPitch && num <= Number(minPitch)) {
              return "Max pitch angle must be greater than min pitch angle";
            }
            return true;
          },
        })}
      />
    </>
  );

  const renderImageCaptureRangeFields = ({
    sliderPitchLimits,
    onPreviewInteraction,
    compactCopy = false,
    boundsOptional = true,
    minDistanceClearable = false,
  }: {
    sliderPitchLimits: { min: number; max: number };
    onPreviewInteraction?: () => void;
    compactCopy?: boolean;
    // When false (leafAreaIndex), the per-bound "Clear" controls are hidden because LAI always
    // persists a full capture range — a cleared bound is silently re-defaulted on save, so the
    // control would be a no-op. "Reset to Default" remains.
    boundsOptional?: boolean;
    // When true (photo), the minimum distance is individually optional and gets its own "Clear Min"
    // control. For imageMeasurement/standCount the minimum distance is always saved, so it is omitted.
    minDistanceClearable?: boolean;
  }) => {
    const distanceErrors = [
      errors.minDistanceMeters?.message ? String(errors.minDistanceMeters.message) : null,
      errors.maxDistanceMeters?.message ? String(errors.maxDistanceMeters.message) : null,
    ].filter(Boolean) as string[];
    const pitchErrors = [
      errors.minPitchAngleDegrees?.message ? String(errors.minPitchAngleDegrees.message) : null,
      errors.maxPitchAngleDegrees?.message ? String(errors.maxPitchAngleDegrees.message) : null,
    ].filter(Boolean) as string[];
    const distanceDescription = compactCopy
      ? "Min is always saved. Max can stay unset."
      : "Set the range where image measurements are reliable. The minimum distance is always saved, and the maximum is optional.";
    const distanceHint = compactCopy
      ? '"--" means the optional max is not saved.'
      : '"--" means that optional bound is not saved. Reset returns the handles to the suggested defaults.';
    const pitchDescription = compactCopy
      ? "Either bound can stay unset."
      : "Show the acceptable tilt range for the phone while the user is aiming at the plant on the ground. The minimum and maximum angles can each be saved independently.";
    const pitchHint = compactCopy
      ? '"--" means that angle bound is not saved.'
      : '"--" means that bound is not saved. Reset returns the handles to the suggested defaults.';

    return (
      <>
        <div className="h-full rounded-xl border bg-background/80 p-4">
          <DualThumbRangeField
            label="Distance From Plant"
            description={distanceDescription}
            min={IMAGE_CAPTURE_DISTANCE_LIMITS.min}
            max={IMAGE_CAPTURE_DISTANCE_LIMITS.max}
            step={IMAGE_CAPTURE_SLIDER_STEP}
            unit="cm"
            values={imageCaptureDistanceRangeValues}
            onValueChange={(values) => {
              onPreviewInteraction?.();
              updateImageCaptureRange("minDistanceMeters", "maxDistanceMeters", values, imageCaptureDistanceRangeValues);
            }}
            thumbDisplayValues={[
              hasMinDistanceCapture ? formatRangeValue(imageCaptureDistanceRangeValues[0], "cm") : "--",
              hasMaxDistanceCapture ? formatRangeValue(imageCaptureDistanceRangeValues[1], "cm") : "--",
            ]}
            minStepsBetweenThumbs={hasMaxDistanceCapture ? 1 : 0}
            actions={
              <>
                {minDistanceClearable && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onPreviewInteraction?.();
                      clearImageCaptureBound("minDistanceMeters");
                    }}
                    disabled={isSystemTrait || !hasMinDistanceCapture}>
                    Clear Min
                  </Button>
                )}
                {boundsOptional && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onPreviewInteraction?.();
                      clearImageCaptureBound("maxDistanceMeters");
                    }}
                    disabled={isSystemTrait || !hasMaxDistanceCapture}>
                    Clear Max
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onPreviewInteraction?.();
                    resetImageCaptureRange("distance");
                  }}
                  disabled={isSystemTrait}>
                  Reset to Default
                </Button>
              </>
            }
            errorMessages={distanceErrors}
            disabled={isSystemTrait}
            suggested={!hasMaxDistanceCapture}
            suggestedHint={distanceHint}
          />
        </div>

        <div className="h-full space-y-4 rounded-xl border bg-background/80 p-4">
          <DualThumbRangeField
            label="Phone Angle for Taking Picture"
            description={pitchDescription}
            min={sliderPitchLimits.min}
            max={sliderPitchLimits.max}
            step={IMAGE_CAPTURE_SLIDER_STEP}
            unit="°"
            values={imageCapturePitchRangeValues}
            onValueChange={(values) => {
              onPreviewInteraction?.();
              updateImageCaptureRange(
                "minPitchAngleDegrees",
                "maxPitchAngleDegrees",
                values,
                imageCapturePitchRangeValues
              );
            }}
            thumbDisplayValues={[
              hasMinPitchCapture
                ? formatRangeValue(parseNumericInput(minPitchAngleDegrees) ?? imageCapturePitchRangeValues[0], "°")
                : "--",
              hasMaxPitchCapture
                ? formatRangeValue(parseNumericInput(maxPitchAngleDegrees) ?? imageCapturePitchRangeValues[1], "°")
                : "--",
            ]}
            minStepsBetweenThumbs={hasMinPitchCapture && hasMaxPitchCapture ? 1 : 0}
            actions={
              <>
                {boundsOptional && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onPreviewInteraction?.();
                      clearImageCaptureBound("minPitchAngleDegrees");
                    }}
                    disabled={isSystemTrait || !hasMinPitchCapture}>
                    Clear Min
                  </Button>
                )}
                {boundsOptional && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onPreviewInteraction?.();
                      clearImageCaptureBound("maxPitchAngleDegrees");
                    }}
                    disabled={isSystemTrait || !hasMaxPitchCapture}>
                    Clear Max
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onPreviewInteraction?.();
                    resetImageCaptureRange("pitch");
                  }}
                  disabled={isSystemTrait}>
                  Reset to Default
                </Button>
              </>
            }
            errorMessages={pitchErrors}
            disabled={isSystemTrait}
            suggested={!hasMinPitchCapture || !hasMaxPitchCapture}
            suggestedHint={pitchHint}
          />
        </div>
      </>
    );
  };

  // Shared capture-requirement section (distance + pitch sliders + visualization) used by the
  // imageMeasurement, photo, and leafAreaIndex traits. Only the pitch limits differ per trait.
  const renderImageCaptureSection = (
    pitchLimits: { min: number; max: number },
    { boundsOptional = true, minDistanceClearable = false }: { boundsOptional?: boolean; minDistanceClearable?: boolean } = {}
  ) => (
    <>
      {renderImageCaptureRequirementInputs(pitchLimits)}
      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          {renderImageCaptureRangeFields({ sliderPitchLimits: displayPitchLimits, boundsOptional, minDistanceClearable })}
        </div>
        {/* The visualization is a distance/angle ground diagram, so only show it once a distance bound
            exists — otherwise (e.g. an unconstrained photo) it would render the fallback range as a
            concrete "good distance" band the trait does not actually enforce. */}
        {(hasMinDistanceCapture || hasMaxDistanceCapture) && (
          <div className="rounded-xl border bg-background/80 p-4 lg:p-5">
            <PitchAngleVisualization
              variant="imageMeasurement"
              minAngle={imageCapturePitchRangeValues[0]}
              maxAngle={imageCapturePitchRangeValues[1]}
              minDistanceCm={imageCaptureDistanceRangeValues[0]}
              maxDistanceCm={imageCaptureDistanceRangeValues[1]}
              hasMinAngle={hasMinPitchCapture}
              hasMaxAngle={hasMaxPitchCapture}
            />
          </div>
        )}
      </div>
    </>
  );

  const minLengthErrorMessage = errors.minLengthMeters?.message ? String(errors.minLengthMeters.message) : undefined;
  const maxLengthErrorMessage = errors.maxLengthMeters?.message ? String(errors.maxLengthMeters.message) : undefined;

  const onFormSubmit = (data: any) => {
    const isSubmittingImageMeasurement = data.type === "imageMeasurement";

    // Auto-add any pending object name before submission
    if (isSubmittingImageMeasurement && data.newObjectName?.trim()) {
      if (!data.objectNames.includes(data.newObjectName.trim())) {
        data.objectNames.push(data.newObjectName.trim());
      }
    }

    // Validate that imageMeasurement and standCountMeasurement have at least one object name
    // Skip validation for standCountMeasurement when skipModelProcessing is enabled
    if (
      (isSubmittingImageMeasurement ||
        (data.type === "standCountMeasurement" && data.standCountModelMode !== "none")) &&
      data.objectNames.length === 0
    ) {
      setObjectNamesError("At least one object to measure is required");
      return;
    }

    // Clear any previous error
    setObjectNamesError(null);

    // Block submit if size grading is being configured but invalid.
    if (data.type === "imageMeasurement" && gradingEditorActive) {
      if (gradingDrafts.length < 2) {
        setGradingSectionOpen(true);
        return;
      }
      if (!gradingValidation.isValid) {
        setGradingSectionOpen(true);
        return;
      }
      // A typed threshold is out of order in the editor (held locally, not emitted).
      if (!gradingEditorValid) {
        setGradingSectionOpen(true);
        return;
      }
    }

    const imageCaptureRequirements = buildImageCaptureRequirements(data);
    const objectDetectionDefaultOptions = buildObjectDetectionDefaultOptions(data);

    const baseConfig = {
      name: data.name,
      description: data.description,
      alwaysShowDescription: data.alwaysShowDescription || undefined, // Only store true values
      type: data.type as EntryConfigType,
      isArchived: data.isArchived,
      metadata: data.metadata,
    };

    const config: any = baseConfig;

    // Add type-specific configurations
    switch (data.type) {
      case "number":
        config.numberConfig = {
          ...(data.wholeNumbersOnly !== undefined && { wholeNumbersOnly: data.wholeNumbersOnly }),
          ...(data.min && { min: Number(data.min) }),
          ...(data.max && { max: Number(data.max) }),
        };
        break;
      case "category":
        config.categoryConfig = {
          choices: data.choices,
          allowMultipleSelections: data.allowMultipleSelections || undefined, // Only store true values
        };
        break;
      case "date":
        config.dateConfig = {
          ...(data.dateMin && { min: data.dateMin }),
          ...(data.dateMax && { max: data.dateMax }),
        };
        break;
      case "imageMeasurement": {
        const confidenceValue = data.confidenceThreshold ? Number(data.confidenceThreshold) / 100 : 0.3;

        const grading =
          gradingEditorActive && gradingDrafts.length > 0
            ? convertDraftsToGrading(gradingDimension, gradingDrafts)
            : undefined;

        config.imageMeasurementConfig = {
          imageMeasurementModelOptions: {
            kind: IMAGE_MEASUREMENT_MODEL_KINDS.OPEN_SET_OBJECT_DETECTOR,
            prompts: data.objectNames,
            hasWeightMeasurement: false,
            ...(data.confidenceThreshold && { confidenceThreshold: confidenceValue }),
          },
          ...(imageCaptureRequirements && { imageCaptureRequirements }),
          ...(objectDetectionDefaultOptions && { objectDetectionDefaultOptions }),
          ...(grading && { grading }),
        };
        break;
      }
      case "standCountMeasurement": {
        if (data.standCountModelMode === "none") {
          config.standCountMeasurementConfig = {
            imgMeasureConfig: {
              imageMeasurementModelOptions: { kind: "none" },
              ...(imageCaptureRequirements && { imageCaptureRequirements }),
            },
            ...(data.boxLengthMeters && { boxLengthMeters: Number(data.boxLengthMeters) / 100 }),
            ...(data.boxWidthMeters && { boxWidthMeters: Number(data.boxWidthMeters) / 100 }),
          };
        } else {
          const confidenceValue = data.confidenceThreshold ? Number(data.confidenceThreshold) / 100 : 0.3;

          config.standCountMeasurementConfig = {
            imgMeasureConfig: {
              imageMeasurementModelOptions: {
                kind: "openSetObjectDetector",
                prompts: data.objectNames,
                hasWeightMeasurement: false,
                ...(data.confidenceThreshold && { confidenceThreshold: confidenceValue }),
              },
              ...(imageCaptureRequirements && { imageCaptureRequirements }),
              ...(objectDetectionDefaultOptions && { objectDetectionDefaultOptions }),
            },
            ...(data.boxLengthMeters && { boxLengthMeters: Number(data.boxLengthMeters) / 100 }),
            ...(data.boxWidthMeters && { boxWidthMeters: Number(data.boxWidthMeters) / 100 }),
          };
        }
        break;
      }
      case "ruler":
        config.rulerConfig = {
          ...(data.rulerMinLengthMeters && { minLengthMeters: Number(data.rulerMinLengthMeters) }),
          ...(data.rulerMaxLengthMeters && { maxLengthMeters: Number(data.rulerMaxLengthMeters) }),
        };
        break;
      case "timer":
        config.timerConfig = {
          startStopOnBarcodeScan: !!data.timerStartStopOnBarcodeScan,
        };
        break;
      case "photo":
        // Generic photo capture requirements are optional; only persist them when the user set a bound.
        config.photoConfig = {
          ...(imageCaptureRequirements && { imageCaptureRequirements }),
        };
        break;
      case "leafAreaIndex": {
        // LAI always persists a full capture-requirement range; fall back to LAI defaults for any unset bound.
        const leafAreaCaptureReqs = {
          minDistanceMeters: imageCaptureRequirements?.minDistanceMeters ?? (LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[0] / 100),
          maxDistanceMeters: imageCaptureRequirements?.maxDistanceMeters ?? (LEAF_AREA_INDEX_DEFAULT_DISTANCE_RANGE[1] / 100),
          minPitchAngleDegrees: imageCaptureRequirements?.minPitchAngleDegrees ?? LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE[0],
          maxPitchAngleDegrees: imageCaptureRequirements?.maxPitchAngleDegrees ?? LEAF_AREA_INDEX_DEFAULT_PITCH_RANGE[1],
        };

        // Retain box dimensions while on-screen mode is active so switching back to fixed mode
        // restores the user's values; the capture flow ignores them unless useFixedBoxSize is true.
        // Also apply defaults for box dimensions and sensitivity.
        const sensitivity = data.leafAreaDefaultSensitivity || "50";
        const boxLength = data.leafAreaBoxLengthMeters || "50";
        const boxWidth = data.leafAreaBoxWidthMeters || "50";

        config.leafAreaIndexConfig = {
          useFixedBoxSize: !!data.leafAreaUseFixedBoxSize,
          defaultSensitivity: Number(sensitivity) / 100,
          boxLengthMeters: Number(boxLength) / 100,
          boxWidthMeters: Number(boxWidth) / 100,
          imageCaptureRequirements: leafAreaCaptureReqs,
        };
        break;
      }
      case "rowPlantedStandCount": {
        const modelOptions =
          data.rowPlantedModelKind === "openSet"
            ? {
                kind: "openSet" as const,
                detectionPrompt: data.rowPlantedDetectionPrompt?.trim() || "plant",
                ...(data.rowPlantedModelSensitivity && {
                  modelSensitivity: Number(data.rowPlantedModelSensitivity) / 100,
                }),
              }
            : {
                kind: "genericCrop" as const,
              };

        config.rowPlantedStandCountConfig = {
          modelOptions,
          // Walk Settings (FIX: use correct field names)
          ...(data.rowPlantedNumPlantLines && { expectedPlantLineCount: Number(data.rowPlantedNumPlantLines) }),
          ...(data.rowPlantedWalkDistanceMeters && {
            walkDistanceMetersTarget: Number(data.rowPlantedWalkDistanceMeters),
          }),
          ...(data.rowPlantedIsWalkDistanceRequired !== undefined && {
            isWalkDistanceRequired: data.rowPlantedIsWalkDistanceRequired,
          }),
          // Plant Spacing & Size (convert cm to meters for storage)
          ...(data.rowPlantedMinSpacingCm && { minPlantSpacingMeters: Number(data.rowPlantedMinSpacingCm) / 100 }),
          ...(data.rowPlantedMinPlantDiameterCm && {
            minPlantDiameterMeters: Number(data.rowPlantedMinPlantDiameterCm) / 100,
          }),
          ...(data.rowPlantedMaxPlantDiameterCm && {
            maxPlantDiameterMeters: Number(data.rowPlantedMaxPlantDiameterCm) / 100,
          }),
          // Detection Distance (convert cm to meters)
          ...(data.rowPlantedMinDetectionDistanceCm && {
            minDetectionDistanceMeters: Number(data.rowPlantedMinDetectionDistanceCm) / 100,
          }),
          ...(data.rowPlantedMaxDetectionDistanceCm && {
            maxDetectionDistanceMeters: Number(data.rowPlantedMaxDetectionDistanceCm) / 100,
          }),
          // Measurement Criteria
          ...(data.rowPlantedMinMeasurementAngle && {
            minMeasurementAngleDegrees: Number(data.rowPlantedMinMeasurementAngle),
          }),
          ...(data.rowPlantedMaxMeasurementAngle && {
            maxMeasurementAngleDegrees: Number(data.rowPlantedMaxMeasurementAngle),
          }),
          ...(data.rowPlantedMaxMeasurementDistanceCm && {
            maxMeasurementDistanceMeters: Number(data.rowPlantedMaxMeasurementDistanceCm) / 100,
          }),
          // Size Filtering
          ...(data.rowPlantedMaxSizePercent && { maxSizePercentOfImage: Number(data.rowPlantedMaxSizePercent) }),
          ...(data.rowPlantedMinSizePercent && { minSizePercentOfImage: Number(data.rowPlantedMinSizePercent) }),
        };
        break;
      }
    }

    onSubmit(config);
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation(); // Prevent bubbling to parent task form dialogs
    void handleSubmit(onFormSubmit)(e);
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      className="space-y-4">
      {isSystemTrait && (
        <div className="bg-muted border border-border rounded-md p-3 mb-4">
          <p className="text-sm font-medium text-muted-foreground">
            System Trait - This is a pre-configured Glean AI trait. Configuration cannot be edited, but you can delete
            traits your organization doesn't need.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label
          htmlFor="name"
          className={errors.name ? "text-destructive" : ""}>
          Trait Name <span className={errors.name ? "text-destructive" : "text-muted-foreground"}>(required)</span>
        </Label>
        <Input
          id="name"
          {...register("name", { required: "Trait name is required" })}
          placeholder="Enter trait name"
          disabled={isSystemTrait}
          className={cn((errors.name || hasDuplicateName) && "border-destructive focus-visible:ring-destructive")}
        />
        {hasDuplicateName && (
          <p className="text-sm text-destructive">
            A trait with this name already exists. Delete the existing one or use a different name.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          {...register("description")}
          placeholder="Enter a description for this trait"
          disabled={isSystemTrait}
        />
      </div>

      <div className="flex items-center space-x-2">
        <Controller
          name="alwaysShowDescription"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value}
              onCheckedChange={field.onChange}
              id="alwaysShowDescription"
              disabled={isSystemTrait}
            />
          )}
        />
        <Label htmlFor="alwaysShowDescription">Always show trait description during data collection</Label>
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">
          Trait Type <span className="text-muted-foreground">(required)</span>
        </Label>
        <Controller
          name="type"
          control={control}
          render={({ field }) => {
            const traitTypes: { value: EntryConfigType; label: string }[] = [
              { value: "text", label: "Text" },
              { value: "number", label: "Number" },
              { value: "category", label: "Category" },
              { value: "date", label: "Date" },
              { value: "boolean", label: "Yes / No" },
              { value: "percentage", label: "Percentage" },
              { value: "photo", label: "Photo" },
              { value: "imageMeasurement", label: "Image Measurement" },
              { value: "standCountMeasurement", label: "Stand Count Measurement" },
              { value: "rowPlantedStandCount", label: "Row Planted Stand Count" },
              { value: "ruler", label: "Ruler" },
              { value: "leafAreaIndex", label: "Leaf Area Index" },
              { value: "timer", label: "Stopwatch" },
            ].filter((type) => !isEntryTypeExcluded(type.value));

            return (
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isSystemTrait}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a trait type" />
                </SelectTrigger>
                <SelectContent>
                  {traitTypes.map((type) => {
                    const Icon = getTraitIcon(type.value);
                    const color = getTraitColor(type.value);
                    return (
                      <SelectItem
                        key={type.value}
                        value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon
                            className={cn("h-4 w-4", color)}
                            aria-hidden="true"
                          />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            );
          }}
        />
      </div>

      {configType === "number" && (
        <div className="space-y-4 p-4 border rounded-md">
          {/* Shortcut buttons */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Sparkles className="h-4 w-4" />
              Shortcuts
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={selectedPreset === "1-to-5" ? "default" : "outline"}
                size="sm"
                onClick={() => applyNumberPreset("1-to-5")}>
                1 to 5 scores
              </Button>
              <Button
                type="button"
                variant={selectedPreset === "1-to-9" ? "default" : "outline"}
                size="sm"
                onClick={() => applyNumberPreset("1-to-9")}>
                1 to 9 scores
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min">Minimum Value</Label>
              <Input
                id="min"
                type="number"
                {...register("min", {
                  validate: (value) => {
                    if (!value) return true;
                    const num = Number(value);
                    const maxVal = watch("max");
                    if (maxVal && num >= Number(maxVal)) {
                      return "Min value must be less than max value";
                    }
                    return true;
                  },
                })}
                placeholder="Min value"
                className={cn(errors.min && "border-red-500")}
              />
              {errors.min && <p className="text-sm text-red-500">{String(errors.min.message)}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="max">Maximum Value</Label>
              <Input
                id="max"
                type="number"
                {...register("max", {
                  validate: (value) => {
                    if (!value) return true;
                    const num = Number(value);
                    const minVal = watch("min");
                    if (minVal && num <= Number(minVal)) {
                      return "Max value must be greater than min value";
                    }
                    return true;
                  },
                })}
                placeholder="Max value"
                className={cn(errors.max && "border-red-500")}
              />
              {errors.max && <p className="text-sm text-red-500">{String(errors.max.message)}</p>}
            </div>
          </div>

          {/* Allow decimals toggle (inverted from wholeNumbersOnly) */}
          <div className="flex items-center space-x-2">
            <Controller
              name="wholeNumbersOnly"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={!field.value}
                  onCheckedChange={(checked) => field.onChange(!checked)}
                  id="allowDecimals"
                />
              )}
            />
            <Label htmlFor="allowDecimals">Allow decimals</Label>
          </div>
        </div>
      )}

      {configType === "category" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Category Options</Label>

          <div className="flex flex-wrap gap-2 mb-2">
            {choices.map((choice, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="px-2 py-1">
                {choice}
                <button
                  type="button"
                  aria-label={`Remove ${choice}`}
                  onClick={() => removeChoice(choice)}
                  className="ml-1 text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {choices.length === 0 && <p className="text-sm text-muted-foreground">No choices added yet</p>}
          </div>

          <div className="flex space-x-2">
            <Input
              {...register("newChoice")}
              placeholder="Add a choice"
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={addChoice}
              disabled={!newChoice.trim()}>
              Add
            </Button>
          </div>

          <div className="flex items-center space-x-2 pt-2">
            <Controller
              name="allowMultipleSelections"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  id="allowMultipleSelections"
                  disabled={isSystemTrait}
                />
              )}
            />
            <Label htmlFor="allowMultipleSelections">Allow multiple options to be selected</Label>
          </div>
        </div>
      )}

      {configType === "date" && (
        <div className="space-y-4 p-4 border rounded-md">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateMin">Minimum Date</Label>
              <Input
                id="dateMin"
                type="date"
                {...register("dateMin", {
                  validate: (value) => {
                    if (!value) return true;
                    const maxDate = watch("dateMax");
                    if (maxDate && value >= maxDate) {
                      return "Min date must be before max date";
                    }
                    return true;
                  },
                })}
                className={cn(errors.dateMin && "border-red-500")}
              />
              {errors.dateMin && <p className="text-sm text-red-500">{String(errors.dateMin.message)}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateMax">Maximum Date</Label>
              <Input
                id="dateMax"
                type="date"
                {...register("dateMax", {
                  validate: (value) => {
                    if (!value) return true;
                    const minDate = watch("dateMin");
                    if (minDate && value <= minDate) {
                      return "Max date must be after min date";
                    }
                    return true;
                  },
                })}
                className={cn(errors.dateMax && "border-red-500")}
              />
              {errors.dateMax && <p className="text-sm text-red-500">{String(errors.dateMax.message)}</p>}
            </div>
          </div>
        </div>
      )}

      {configType === "imageMeasurement" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Image Measurement Configuration</Label>

          <div className="space-y-4">
            <ObjectDetectionConfigSection
              objectNames={objectNames}
              newObjectName={newObjectName}
              objectNamesError={objectNamesError}
              confidenceThreshold={confidenceThreshold}
              advancedOptionsOpen={advancedOptionsOpen}
              isSystemTrait={isSystemTrait}
              newObjectNameInputProps={newObjectNameField}
              minLengthInputProps={minLengthMetersField}
              maxLengthInputProps={maxLengthMetersField}
              minLengthError={minLengthErrorMessage}
              maxLengthError={maxLengthErrorMessage}
              onAddObjectName={addObjectName}
              onRemoveObjectName={removeObjectName}
              onConfidenceChange={(value) => setValue("confidenceThreshold", value, RANGE_UPDATE_OPTIONS)}
              onAdvancedOptionsOpenChange={setAdvancedOptionsOpen}
              showAdvancedOptions={false}
            />

            <div>
              <h4 className="font-medium text-sm mb-2">Image Capture Configuration</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Configure how users take photos to ensure imagery is standardized
              </p>

              {renderImageCaptureSection(IMAGE_MEASUREMENT_PITCH_LIMITS)}
            </div>

            <ImageMeasurementGradingSection
              dimension={gradingDimension}
              drafts={gradingDrafts}
              validation={gradingValidation}
              isOpen={gradingSectionOpen}
              isSystemTrait={isSystemTrait}
              onDimensionChange={setGradingDimension}
              onDraftsChange={setGradingDrafts}
              onOpenChange={setGradingSectionOpen}
              onValidityChange={setGradingEditorValid}
              onActiveChange={setGradingEditorActive}
            />

            <ObjectDetectionAdvancedOptions
              confidenceThreshold={confidenceThreshold}
              advancedOptionsOpen={advancedOptionsOpen}
              isSystemTrait={isSystemTrait}
              minLengthInputProps={minLengthMetersField}
              maxLengthInputProps={maxLengthMetersField}
              minLengthError={minLengthErrorMessage}
              maxLengthError={maxLengthErrorMessage}
              onConfidenceChange={(value) => setValue("confidenceThreshold", value, RANGE_UPDATE_OPTIONS)}
              onAdvancedOptionsOpenChange={setAdvancedOptionsOpen}
            />
          </div>
        </div>
      )}

      {configType === "photo" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Photo Capture Configuration</Label>
          <p className="text-sm text-muted-foreground">
            Optionally set the distance and phone angle range so photos are taken consistently. Leave a bound unset to
            allow any distance or angle.
          </p>

          {renderImageCaptureSection(IMAGE_MEASUREMENT_PITCH_LIMITS, { minDistanceClearable: true })}
        </div>
      )}

      {configType === "ruler" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Ruler Configuration</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rulerMinLengthMeters">Min Length (meters)</Label>
              <Input
                id="rulerMinLengthMeters"
                type="number"
                step="0.01"
                {...register("rulerMinLengthMeters", {
                  validate: (value) => {
                    if (!value) return true;
                    const num = Number(value);
                    if (num <= 0) {
                      return "Min length must be greater than 0";
                    }
                    const maxLen = watch("rulerMaxLengthMeters");
                    if (maxLen && num >= Number(maxLen)) {
                      return "Min length must be less than max length";
                    }
                    return true;
                  },
                })}
                placeholder="Min length"
                className={cn(errors.rulerMinLengthMeters && "border-red-500")}
              />
              {errors.rulerMinLengthMeters && (
                <p className="text-sm text-red-500">{String(errors.rulerMinLengthMeters.message)}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rulerMaxLengthMeters">Max Length (meters)</Label>
              <Input
                id="rulerMaxLengthMeters"
                type="number"
                step="0.01"
                {...register("rulerMaxLengthMeters", {
                  validate: (value) => {
                    if (!value) return true;
                    const num = Number(value);
                    if (num <= 0) {
                      return "Max length must be greater than 0";
                    }
                    const minLen = watch("rulerMinLengthMeters");
                    if (minLen && num <= Number(minLen)) {
                      return "Max length must be greater than min length";
                    }
                    return true;
                  },
                })}
                placeholder="Max length"
                className={cn(errors.rulerMaxLengthMeters && "border-red-500")}
              />
              {errors.rulerMaxLengthMeters && (
                <p className="text-sm text-red-500">{String(errors.rulerMaxLengthMeters.message)}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {configType === "timer" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Stopwatch Configuration</Label>
          <div className="flex items-center space-x-2 pt-2">
            <Controller
              name="timerStartStopOnBarcodeScan"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  id="timerStartStopOnBarcodeScan"
                  disabled={isSystemTrait}
                />
              )}
            />
            <Label htmlFor="timerStartStopOnBarcodeScan">
              Start / stop the stopwatch when a barcode is scanned
            </Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Barcode start/stop is handled on the mobile app during collection. The
            web app stores and displays stopwatch durations but never runs the stopwatch.
          </p>
        </div>
      )}

      {configType === "leafAreaIndex" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Leaf Area Index Configuration</Label>

          <div className="space-y-2 rounded-xl border bg-background/80 p-4">
            <div className="flex items-center space-x-2">
              <Controller
                name="leafAreaUseFixedBoxSize"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    id="leafAreaUseFixedBoxSize"
                    disabled={isSystemTrait}
                  />
                )}
              />
              <Label htmlFor="leafAreaUseFixedBoxSize">Use fixed box size</Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Off uses an on-screen box for percentage coverage. On places a fixed-size box on the ground that stays
              in place as you move the camera.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="leafAreaDefaultSensitivity">Default Sensitivity</Label>
              <span className="text-sm text-muted-foreground">{leafAreaDefaultSensitivity}%</span>
            </div>
            <Controller
              name="leafAreaDefaultSensitivity"
              control={control}
              render={({ field }) => (
                <Slider
                  id="leafAreaDefaultSensitivity"
                  min={0}
                  max={100}
                  step={1}
                  value={[Number(field.value)]}
                  onValueChange={(values) => field.onChange(values[0].toString())}
                  className="w-full"
                  disabled={isSystemTrait}
                />
              )}
            />
          </div>

          {leafAreaUseFixedBoxSize && (
            <div>
              <div className="mb-2">
                <h5 className="font-medium text-sm">Size of Sampled Area</h5>
                <p className="text-sm text-muted-foreground">
                  Define the default size of the sampling area for leaf area measurements
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="leafAreaBoxLengthMeters">Box Length (cm)</Label>
                  <Input
                    id="leafAreaBoxLengthMeters"
                    type="number"
                    step="1"
                    {...register("leafAreaBoxLengthMeters", {
                      validate: (value) => {
                        if (!value) {
                          return leafAreaUseFixedBoxSize ? "Box length is required for a fixed box" : true;
                        }
                        const num = Number(value);
                        if (!Number.isFinite(num)) {
                          return "Box length must be a finite number";
                        }
                        if (num < 10) {
                          return "Box length must be at least 10 cm";
                        }
                        if (num > 200) {
                          return "Box length cannot exceed 200 cm (2 meters)";
                        }
                        return true;
                      },
                    })}
                    placeholder="Box length (cm)"
                    className={cn(errors.leafAreaBoxLengthMeters && "border-red-500")}
                    disabled={isSystemTrait}
                  />
                  {errors.leafAreaBoxLengthMeters && (
                    <p className="text-sm text-destructive">{errors.leafAreaBoxLengthMeters.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="leafAreaBoxWidthMeters">Box Width (cm)</Label>
                  <Input
                    id="leafAreaBoxWidthMeters"
                    type="number"
                    step="1"
                    {...register("leafAreaBoxWidthMeters", {
                      validate: (value) => {
                        if (!value) {
                          return leafAreaUseFixedBoxSize ? "Box width is required for a fixed box" : true;
                        }
                        const num = Number(value);
                        if (!Number.isFinite(num)) {
                          return "Box width must be a finite number";
                        }
                        if (num < 10) {
                          return "Box width must be at least 10 cm";
                        }
                        if (num > 200) {
                          return "Box width cannot exceed 200 cm (2 meters)";
                        }
                        return true;
                      },
                    })}
                    placeholder="Box width (cm)"
                    className={cn(errors.leafAreaBoxWidthMeters && "border-red-500")}
                    disabled={isSystemTrait}
                  />
                  {errors.leafAreaBoxWidthMeters && (
                    <p className="text-sm text-destructive">{errors.leafAreaBoxWidthMeters.message}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="mb-2">
              <h5 className="font-medium text-sm">Image Capture Requirements</h5>
              <p className="text-sm text-muted-foreground">
                Configure camera distance and angle constraints for optimal measurements
              </p>
            </div>

            {renderImageCaptureSection(LEAF_AREA_INDEX_PITCH_LIMITS, { boundsOptional: false })}
          </div>
        </div>
      )}

      {configType === "standCountMeasurement" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Stand Count Measurement Configuration</Label>

          <div className="space-y-2">
            <Label>Detection Mode</Label>
            <Controller
              name="standCountModelMode"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isSystemTrait}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openSet">Object Detection</SelectItem>
                    <SelectItem value="none">Manual Annotation Only</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {standCountModelMode === "none" && (
              <p className="text-xs text-muted-foreground">
                Capture images for manual annotation only — no automated plant detection
              </p>
            )}
          </div>

          <div className="space-y-4">
            {standCountModelMode !== "none" && (
              <ObjectDetectionConfigSection
                objectNames={objectNames}
                newObjectName={newObjectName}
                objectNamesError={objectNamesError}
                confidenceThreshold={confidenceThreshold}
                advancedOptionsOpen={advancedOptionsOpen}
                isSystemTrait={isSystemTrait}
                newObjectNameInputProps={newObjectNameField}
                minLengthInputProps={minLengthMetersField}
                maxLengthInputProps={maxLengthMetersField}
                minLengthError={minLengthErrorMessage}
                maxLengthError={maxLengthErrorMessage}
                onAddObjectName={addObjectName}
                onRemoveObjectName={removeObjectName}
                onConfidenceChange={(value) => setValue("confidenceThreshold", value, RANGE_UPDATE_OPTIONS)}
                onAdvancedOptionsOpenChange={setAdvancedOptionsOpen}
              />
            )}

            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="font-medium text-sm">Stand Count Capture Setup</h4>
                <p className="text-sm text-muted-foreground">
                  Adjust phone distance, phone angle, and the stand count box together.
                </p>
              </div>

              {renderImageCaptureRequirementInputs(STAND_COUNT_PITCH_LIMITS)}
              <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.15fr)_minmax(260px,0.9fr)]">
                {renderImageCaptureRangeFields({
                  sliderPitchLimits: displayPitchLimits,
                  onPreviewInteraction: pingStandCountPreviewPause,
                  compactCopy: true,
                })}

                <div className="space-y-4 rounded-xl border bg-background/80 p-4">
                  <div className="space-y-1">
                    <h5 className="font-medium text-sm">Stand Count Box</h5>
                    <p className="text-sm text-muted-foreground">The longer side is shown on the ground in the preview.</p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="boxLengthMeters">Box Length (cm)</Label>
                      <Input
                        id="boxLengthMeters"
                        type="number"
                        step="1"
                        {...register("boxLengthMeters", {
                          onChange: () => pingStandCountPreviewPause(),
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < 5) {
                              return "Box length must be at least 5 cm";
                            }
                            if (num > 200) {
                              return "Box length cannot exceed 200 cm (2 meters)";
                            }
                            return true;
                          },
                        })}
                        placeholder="Box length (cm)"
                        className={cn(errors.boxLengthMeters && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.boxLengthMeters && (
                        <p className="text-sm text-red-500">{String(errors.boxLengthMeters.message)}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="boxWidthMeters">Box Width (cm)</Label>
                      <Input
                        id="boxWidthMeters"
                        type="number"
                        step="1"
                        {...register("boxWidthMeters", {
                          onChange: () => pingStandCountPreviewPause(),
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < 5) {
                              return "Box width must be at least 5 cm";
                            }
                            if (num > 200) {
                              return "Box width cannot exceed 200 cm (2 meters)";
                            }
                            return true;
                          },
                        })}
                        placeholder="Box width (cm)"
                        className={cn(errors.boxWidthMeters && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.boxWidthMeters && (
                        <p className="text-sm text-red-500">{String(errors.boxWidthMeters.message)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-background/80 p-4 lg:p-5">
              <PitchAngleVisualization
                variant="standCount"
                minAngle={imageCapturePitchRangeValues[0]}
                maxAngle={imageCapturePitchRangeValues[1]}
                minDistanceCm={imageCaptureDistanceRangeValues[0]}
                maxDistanceCm={imageCaptureDistanceRangeValues[1]}
                boxSizeCm={standCountBoxLongestSideCm}
                hasMinAngle={hasMinPitchCapture}
                hasMaxAngle={hasMaxPitchCapture}
                paused={standCountPreviewPaused}
              />
            </div>
          </div>
        </div>
      )}

      {configType === "rowPlantedStandCount" && (
        <div className="space-y-4 p-4 border rounded-md">
          <Label>Row Planted Stand Count Configuration</Label>
          <p className="text-sm text-muted-foreground">Configure the AR walking measurement for row-planted crops</p>

          <div className="space-y-4">
            {/* Section 1: Detection Model */}
            <div className="space-y-2">
              <Label>Detection Model</Label>
              <Controller
                name="rowPlantedModelKind"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isSystemTrait}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select model type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="genericCrop">
                        <div className="flex flex-col items-start">
                          <span>Generic Crop</span>
                          <span className="text-xs text-muted-foreground">Pre-trained model for common crops</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="openSet">
                        <div className="flex flex-col items-start">
                          <span>Open Set (Custom)</span>
                          <span className="text-xs text-muted-foreground">Custom detection with text prompt</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {rowPlantedModelKind === "openSet" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="rowPlantedDetectionPrompt">Detection Prompt</Label>
                  <Input
                    {...register("rowPlantedDetectionPrompt")}
                    id="rowPlantedDetectionPrompt"
                    placeholder="e.g., plant, corn, soybean"
                    disabled={isSystemTrait}
                  />
                  <p className="text-xs text-muted-foreground">
                    The object type to detect (e.g., &quot;plant&quot;, &quot;corn&quot;, &quot;soybean&quot;). More
                    specific prompts can improve detection accuracy.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="rowPlantedModelSensitivity">Model Sensitivity</Label>
                    <span className="text-sm text-muted-foreground">{rowPlantedModelSensitivity}%</span>
                  </div>
                  <Controller
                    name="rowPlantedModelSensitivity"
                    control={control}
                    render={({ field }) => (
                      <Slider
                        id="rowPlantedModelSensitivity"
                        min={0}
                        max={100}
                        step={1}
                        value={[Number(field.value)]}
                        onValueChange={(values) => field.onChange(values[0].toString())}
                        className="w-full"
                        disabled={isSystemTrait}
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    How sensitive the AI model is when detecting plants. Higher values detect more plants but may
                    include false positives.
                  </p>
                </div>
              </>
            )}

            <Separator />

            {/* Section 2: Walk Settings */}
            <div className="space-y-2">
              <h5 className="font-medium text-sm">Walk Settings</h5>
              <p className="text-sm text-muted-foreground">Configure the walking measurement parameters</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rowPlantedNumPlantLines">Plant Lines</Label>
                <Input
                  id="rowPlantedNumPlantLines"
                  type="number"
                  step="1"
                  min="1"
                  max="6"
                  {...register("rowPlantedNumPlantLines", {
                    validate: (value) => {
                      if (!value) return true;
                      const num = Number(value);
                      if (num < 1) return "Must be at least 1";
                      if (num > 6) return "Cannot exceed 6 lines";
                      if (!Number.isInteger(num)) return "Must be a whole number";
                      return true;
                    },
                  })}
                  placeholder="e.g., 2"
                  className={cn(errors.rowPlantedNumPlantLines && "border-red-500")}
                  disabled={isSystemTrait}
                />
                <p className="text-xs text-muted-foreground">
                  Number of parallel plant rows being measured. Set to 1 for a single row, or higher for multiple
                  adjacent rows.
                </p>
                {errors.rowPlantedNumPlantLines && (
                  <p className="text-sm text-red-500">{String(errors.rowPlantedNumPlantLines.message)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="rowPlantedWalkDistanceMeters">Walk Distance (m)</Label>
                <Input
                  id="rowPlantedWalkDistanceMeters"
                  type="number"
                  step="0.5"
                  min="1"
                  max="100"
                  {...register("rowPlantedWalkDistanceMeters", {
                    validate: (value) => {
                      if (!value) return true;
                      const num = Number(value);
                      if (num < 1) return "Must be at least 1m";
                      if (num > 100) return "Cannot exceed 100m";
                      return true;
                    },
                  })}
                  placeholder="e.g., 10"
                  className={cn(errors.rowPlantedWalkDistanceMeters && "border-red-500")}
                  disabled={isSystemTrait}
                />
                <p className="text-xs text-muted-foreground">
                  Target distance to walk while collecting stand count data. Longer distances provide more
                  representative samples.
                </p>
                {errors.rowPlantedWalkDistanceMeters && (
                  <p className="text-sm text-red-500">{String(errors.rowPlantedWalkDistanceMeters.message)}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Controller
                name="rowPlantedIsWalkDistanceRequired"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    id="rowPlantedIsWalkDistanceRequired"
                    disabled={isSystemTrait}
                  />
                )}
              />
              <div>
                <Label htmlFor="rowPlantedIsWalkDistanceRequired">Require Walk Distance</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, measurement cannot be completed until target distance is reached
                </p>
              </div>
            </div>

            {/* Section 3: Advanced Detection Parameters (Collapsible) */}
            <Collapsible
              open={rowPlantedAdvancedOpen}
              onOpenChange={setRowPlantedAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="flex items-center gap-2 w-full justify-start p-0 hover:bg-transparent">
                  <ChevronRight className={cn("h-4 w-4 transition-transform", rowPlantedAdvancedOpen && "rotate-90")} />
                  <span className="text-sm font-medium">Advanced Detection Parameters</span>
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 mt-4">
                {/* Plant Spacing & Size */}
                <div className="space-y-1">
                  <h6 className="font-semibold text-sm text-foreground">Plant Spacing & Size</h6>
                  <div className="h-px bg-border" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rowPlantedMinSpacingCm">Min Spacing Between Plants (cm)</Label>
                  <Input
                    id="rowPlantedMinSpacingCm"
                    type="number"
                    step="1"
                    {...register("rowPlantedMinSpacingCm", {
                      validate: (value) => {
                        if (!value) return true;
                        const num = Number(value);
                        if (num < 5) return "Must be at least 5 cm";
                        if (num > 200) return "Cannot exceed 200 cm";
                        return true;
                      },
                    })}
                    placeholder="e.g., 15"
                    className={cn(errors.rowPlantedMinSpacingCm && "border-red-500")}
                    disabled={isSystemTrait}
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum expected distance between adjacent plants in a row. Detections closer than this will be
                    merged as duplicates.
                  </p>
                  {errors.rowPlantedMinSpacingCm && (
                    <p className="text-sm text-red-500">{String(errors.rowPlantedMinSpacingCm.message)}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Allowable Plant Diameter Sizes (cm)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Expected range of plant diameters. Plants outside this range may be filtered out or flagged for
                    review.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="rowPlantedMinPlantDiameterCm"
                        className="text-xs">
                        Min Diameter
                      </Label>
                      <Input
                        id="rowPlantedMinPlantDiameterCm"
                        type="number"
                        step="1"
                        {...register("rowPlantedMinPlantDiameterCm", {
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < 3) return "Must be at least 3 cm";
                            if (num > 100) return "Cannot exceed 100 cm";
                            return true;
                          },
                        })}
                        placeholder="e.g., 5"
                        className={cn(errors.rowPlantedMinPlantDiameterCm && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.rowPlantedMinPlantDiameterCm && (
                        <p className="text-sm text-red-500">{String(errors.rowPlantedMinPlantDiameterCm.message)}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="rowPlantedMaxPlantDiameterCm"
                        className="text-xs">
                        Max Diameter
                      </Label>
                      <Input
                        id="rowPlantedMaxPlantDiameterCm"
                        type="number"
                        step="1"
                        {...register("rowPlantedMaxPlantDiameterCm", {
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < 3) return "Must be at least 3 cm";
                            if (num > 100) return "Cannot exceed 100 cm";
                            return true;
                          },
                        })}
                        placeholder="e.g., 50"
                        className={cn(errors.rowPlantedMaxPlantDiameterCm && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.rowPlantedMaxPlantDiameterCm && (
                        <p className="text-sm text-red-500">{String(errors.rowPlantedMaxPlantDiameterCm.message)}</p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Detection Distance */}
                <div className="space-y-1">
                  <h6 className="font-semibold text-sm text-foreground">Detection Distance</h6>
                  <div className="h-px bg-border" />
                </div>

                <div className="space-y-2">
                  <Label>Allowable Plant Distance from Camera (cm)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Range of distances from the camera where plants will be detected. Setting a maximum prevents
                    accidentally detecting background plants in rows not being measured.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="rowPlantedMinDetectionDistanceCm"
                        className="text-xs">
                        Min Distance
                      </Label>
                      <Input
                        id="rowPlantedMinDetectionDistanceCm"
                        type="number"
                        step="1"
                        {...register("rowPlantedMinDetectionDistanceCm", {
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < 30) return "Must be at least 30 cm";
                            if (num > 500) return "Cannot exceed 500 cm (5m)";
                            return true;
                          },
                        })}
                        placeholder="e.g., 30"
                        className={cn(errors.rowPlantedMinDetectionDistanceCm && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.rowPlantedMinDetectionDistanceCm && (
                        <p className="text-sm text-red-500">
                          {String(errors.rowPlantedMinDetectionDistanceCm.message)}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="rowPlantedMaxDetectionDistanceCm"
                        className="text-xs">
                        Max Distance
                      </Label>
                      <Input
                        id="rowPlantedMaxDetectionDistanceCm"
                        type="number"
                        step="1"
                        {...register("rowPlantedMaxDetectionDistanceCm", {
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < 30) return "Must be at least 30 cm";
                            if (num > 500) return "Cannot exceed 500 cm (5m)";
                            return true;
                          },
                        })}
                        placeholder="e.g., 200"
                        className={cn(errors.rowPlantedMaxDetectionDistanceCm && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.rowPlantedMaxDetectionDistanceCm && (
                        <p className="text-sm text-red-500">
                          {String(errors.rowPlantedMaxDetectionDistanceCm.message)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Measurement Criteria */}
                <div className="space-y-1">
                  <h6 className="font-semibold text-sm text-foreground">Measurement Criteria</h6>
                  <div className="h-px bg-border" />
                </div>

                <div className="space-y-2">
                  <Label>Measurement Angle (degrees)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Camera angle range from directly overhead (nadir) for capturing plant metrics. Lower angles provide
                    more accurate diameter measurements.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="rowPlantedMinMeasurementAngle"
                        className="text-xs">
                        Min Angle
                      </Label>
                      <Input
                        id="rowPlantedMinMeasurementAngle"
                        type="number"
                        step="1"
                        {...register("rowPlantedMinMeasurementAngle", {
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < -20) return "Must be at least -20°";
                            if (num > 180) return "Cannot exceed 180°";
                            return true;
                          },
                        })}
                        placeholder="e.g., 0"
                        className={cn(errors.rowPlantedMinMeasurementAngle && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.rowPlantedMinMeasurementAngle && (
                        <p className="text-sm text-red-500">{String(errors.rowPlantedMinMeasurementAngle.message)}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="rowPlantedMaxMeasurementAngle"
                        className="text-xs">
                        Max Angle
                      </Label>
                      <Input
                        id="rowPlantedMaxMeasurementAngle"
                        type="number"
                        step="1"
                        {...register("rowPlantedMaxMeasurementAngle", {
                          validate: (value) => {
                            if (!value) return true;
                            const num = Number(value);
                            if (num < -20) return "Must be at least -20°";
                            if (num > 180) return "Cannot exceed 180°";
                            return true;
                          },
                        })}
                        placeholder="e.g., 45"
                        className={cn(errors.rowPlantedMaxMeasurementAngle && "border-red-500")}
                        disabled={isSystemTrait}
                      />
                      {errors.rowPlantedMaxMeasurementAngle && (
                        <p className="text-sm text-red-500">{String(errors.rowPlantedMaxMeasurementAngle.message)}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rowPlantedMaxMeasurementDistanceCm">Max Measurement Distance (cm)</Label>
                  <Input
                    id="rowPlantedMaxMeasurementDistanceCm"
                    type="number"
                    step="1"
                    {...register("rowPlantedMaxMeasurementDistanceCm", {
                      validate: (value) => {
                        if (!value) return true;
                        const num = Number(value);
                        if (num < 30) return "Must be at least 30 cm";
                        if (num > 400) return "Cannot exceed 400 cm (4m)";
                        return true;
                      },
                    })}
                    placeholder="e.g., 150"
                    className={cn(errors.rowPlantedMaxMeasurementDistanceCm && "border-red-500")}
                    disabled={isSystemTrait}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum distance from camera for capturing plant diameter and health metrics. Closer measurements
                    are more accurate.
                  </p>
                  {errors.rowPlantedMaxMeasurementDistanceCm && (
                    <p className="text-sm text-red-500">{String(errors.rowPlantedMaxMeasurementDistanceCm.message)}</p>
                  )}
                </div>

                <Separator />

                {/* Size Filtering */}
                <div className="space-y-1">
                  <h6 className="font-semibold text-sm text-foreground">Size Filtering</h6>
                  <div className="h-px bg-border" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rowPlantedMinSizePercent">Min Detection Size (%)</Label>
                    <Input
                      id="rowPlantedMinSizePercent"
                      type="number"
                      step="0.1"
                      {...register("rowPlantedMinSizePercent", {
                        validate: (value) => {
                          if (!value) return true;
                          const num = Number(value);
                          if (num < 0.1) return "Must be at least 0.1%";
                          if (num > 10) return "Cannot exceed 10%";
                          return true;
                        },
                      })}
                      placeholder="e.g., 0.5"
                      className={cn(errors.rowPlantedMinSizePercent && "border-red-500")}
                      disabled={isSystemTrait}
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimum size of a detection as a percentage of the image. Filters out tiny detections that may be
                      noise.
                    </p>
                    {errors.rowPlantedMinSizePercent && (
                      <p className="text-sm text-red-500">{String(errors.rowPlantedMinSizePercent.message)}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rowPlantedMaxSizePercent">Max Detection Size (%)</Label>
                    <Input
                      id="rowPlantedMaxSizePercent"
                      type="number"
                      step="1"
                      {...register("rowPlantedMaxSizePercent", {
                        validate: (value) => {
                          if (!value) return true;
                          const num = Number(value);
                          if (num < 1) return "Must be at least 1%";
                          if (num > 100) return "Cannot exceed 100%";
                          return true;
                        },
                      })}
                      placeholder="e.g., 50"
                      className={cn(errors.rowPlantedMaxSizePercent && "border-red-500")}
                      disabled={isSystemTrait}
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum size of a detection as a percentage of the image. Filters out overly large detections that
                      may be false positives.
                    </p>
                    {errors.rowPlantedMaxSizePercent && (
                      <p className="text-sm text-red-500">{String(errors.rowPlantedMaxSizePercent.message)}</p>
                    )}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      )}

      <Separator className="my-4" />

      <div className="flex justify-between">
        {showDelete && onDelete && (
          <Button
            type="button"
            variant="outline"
            onClick={onDelete}
            className="text-destructive hover:text-destructive">
            <Archive className="h-4 w-4 mr-2" />
            Remove Trait
          </Button>
        )}
        <div className="flex space-x-2 ml-auto">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}>
            {isSystemTrait ? "Close" : "Cancel"}
          </Button>
          {!isSystemTrait && (
            <Button
              type="submit"
              disabled={hasDuplicateName}
              className="bg-agri-green hover:bg-agri-green-dark">
              Save Trait
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};

export default AddTraitForm;
